import {
  formatAmount,
  formatDateTime,
  formatValue,
  getEnumLabel,
} from "~/common/utils"
import { Descriptions, Empty, Flex, Table, Tag, Typography } from "antd"
import type { TableProps } from "antd"
import {
  BulkWalletEntryMode,
  OrderSource,
  OrderType,
  WalletRechargeType,
} from "~/common/constants"
import {
  bulkEntryModeLabel,
  fulfillmentStatusLabel,
  orderSourceLabel,
  orderStatusLabel,
  orderTypeLabel,
  paymentMethodLabel,
  paymentStatusLabel,
  productCodeLabel,
  rechargeTypeLabel,
} from "~/pages/Staff/labels"
import styles from "./index.css"

const { Text, Title } = Typography

const cleanupRechargeStatusLabel: Record<number, string> = {
  1: "等待生效",
  2: "有效",
  3: "已过期",
  4: "已撤销",
}

const refundStatusLabel: Record<API.Staff.StaffOrderCleanupStatus, string> = {
  PROCESSING: "操作处理中",
  SUCCESS: "退款申请已受理",
  CONFIRMED: "已人工确认到账",
  FAILED: "退款申请失败",
}

const refundStatusColor: Record<API.Staff.StaffOrderCleanupStatus, string> = {
  PROCESSING: "gold",
  SUCCESS: "blue",
  CONFIRMED: "green",
  FAILED: "red",
}

const rechargeColumns: TableProps<API.User.WalletRechargeRecord>["columns"] = [
  {
    title: "充值记录ID",
    dataIndex: "id",
    width: 210,
    render: formatValue,
  },
  {
    title: "类型",
    dataIndex: "recharge_type",
    width: 150,
    render: (value: WalletRechargeType) => rechargeTypeLabel[value] || value,
  },
  {
    title: "状态",
    dataIndex: "status",
    width: 110,
    render: (value?: number) => getEnumLabel(cleanupRechargeStatusLabel, value),
  },
  { title: "充值积点", dataIndex: "recharge_amount", width: 100 },
  { title: "剩余", dataIndex: "remaining_amount", width: 90 },
  { title: "冻结", dataIndex: "frozen_amount", width: 90 },
  { title: "已使用", dataIndex: "used_amount", width: 90 },
  {
    title: "生效时间",
    dataIndex: "effective_at",
    width: 180,
    render: formatDateTime,
  },
  {
    title: "到期时间",
    dataIndex: "expires_at",
    width: 180,
    render: formatDateTime,
  },
]

export const CleanupGuidance = () => {
  return (
    <Flex
      className={styles.guidance}
      gap={16}
      wrap
    >
      <section className={styles.guidanceItem}>
        <Text strong>1. 核对可清理范围</Text>
        <Text type="secondary">
          仅支持权益未使用的 Staff
          测试账号在线订单；专业版连续购买须从尾部倒序处理。
        </Text>
      </section>
      <section className={styles.guidanceItem}>
        <Text strong>2. 原路发起微信退款</Text>
        <Text type="secondary">
          后端校验订单和支付记录后发起全额退款，页面会保留商户退款单号和微信退款状态。
        </Text>
      </section>
      <section className={styles.guidanceItem}>
        <Text strong>3. 确认到账后删除</Text>
        <Text type="secondary">
          人工核实用户退款到账并确认后，才能永久删除订单、支付记录和关联充值记录。
        </Text>
      </section>
    </Flex>
  )
}

export const RefundDetails = ({
  refund,
}: {
  refund?: API.Staff.OrderCleanupRefundInfo | null
}) => {
  if (!refund) return null

  return (
    <section className={styles.detailSection}>
      <Title
        className={styles.detailTitle}
        level={4}
      >
        退款操作
      </Title>
      <Descriptions
        bordered
        column={3}
        size="small"
      >
        <Descriptions.Item label="操作状态">
          <Tag color={refundStatusColor[refund.status]}>
            {refundStatusLabel[refund.status]}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="微信退款状态">
          {refund.wechat_refund_status ? (
            <Tag
              color={
                refund.wechat_refund_status === "SUCCESS" ? "green" : "blue"
              }
            >
              {refund.wechat_refund_status}
            </Tag>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="退款操作记录ID">
          {refund.op_log_id}
        </Descriptions.Item>
        <Descriptions.Item label="商户退款单号">
          {formatValue(refund.out_refund_no)}
        </Descriptions.Item>
        <Descriptions.Item label="微信退款单号">
          {formatValue(refund.refund_id)}
        </Descriptions.Item>
        <Descriptions.Item label="发起时间">
          {formatDateTime(refund.requested_at)}
        </Descriptions.Item>
        <Descriptions.Item label="确认到账时间">
          {formatDateTime(refund.confirmed_at)}
        </Descriptions.Item>
        <Descriptions.Item label="确认人手机号">
          {formatValue(refund.confirmed_by_staff_mobile)}
        </Descriptions.Item>
        <Descriptions.Item label="确认人ID">
          {formatValue(refund.confirmed_by_staff_id)}
        </Descriptions.Item>
        {refund.error_message ? (
          <Descriptions.Item
            label="失败原因"
            span={3}
          >
            <Text type="danger">{refund.error_message}</Text>
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    </section>
  )
}

export const OrderDetails = ({ order }: { order: API.User.UserOrder }) => {
  const levelPackage = order.level_package

  return (
    <section className={styles.detailSection}>
      <Title
        className={styles.detailTitle}
        level={4}
      >
        订单信息
      </Title>
      <Descriptions
        bordered
        column={3}
        size="small"
      >
        <Descriptions.Item label="订单号">{order.order_no}</Descriptions.Item>
        <Descriptions.Item label="订单ID">
          {formatValue(order.id)}
        </Descriptions.Item>
        <Descriptions.Item label="用户ID">{order.user_id}</Descriptions.Item>
        <Descriptions.Item label="订单类型">
          {orderTypeLabel[order.order_type] || order.order_type}
        </Descriptions.Item>
        <Descriptions.Item label="商品">
          {getEnumLabel(productCodeLabel, order.product_code)}
        </Descriptions.Item>
        <Descriptions.Item label="金额">
          {formatAmount(order.amount)}
        </Descriptions.Item>
        <Descriptions.Item label="订单状态">
          {getEnumLabel(orderStatusLabel, order.status)}
        </Descriptions.Item>
        <Descriptions.Item label="履约状态">
          {getEnumLabel(fulfillmentStatusLabel, order.fulfillment_status)}
        </Descriptions.Item>
        <Descriptions.Item label="订单来源">
          <Tag
            color={
              order.source === OrderSource.onlinePurchase ? "blue" : "orange"
            }
          >
            {orderSourceLabel[order.source] || order.source}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="购买内容">
          {formatValue(order.description)}
        </Descriptions.Item>
        <Descriptions.Item label="支付时间">
          {formatDateTime(order.paid_at)}
        </Descriptions.Item>
        <Descriptions.Item label="创建时间">
          {formatDateTime(order.created_t)}
        </Descriptions.Item>
        {order.order_type === OrderType.walletPackage ? (
          <Descriptions.Item label="充值积点">
            {formatValue(order.wallet_package?.wallet_recharge_amount)}
          </Descriptions.Item>
        ) : null}
        {order.bulk_wallet_package ? (
          <>
            <Descriptions.Item label="录入方式">
              {
                bulkEntryModeLabel[
                  order.bulk_wallet_package.entry_mode ??
                    BulkWalletEntryMode.quantity
                ]
              }
            </Descriptions.Item>
            <Descriptions.Item label="购买积点">
              {order.bulk_wallet_package.wallet_recharge_amount}
            </Descriptions.Item>
            <Descriptions.Item label="购买单价">
              {order.bulk_wallet_package.purchase_unit_price} 分/积点
            </Descriptions.Item>
            <Descriptions.Item label="退款结算单价">
              {order.bulk_wallet_package.refund_unit_price} 分/积点
            </Descriptions.Item>
          </>
        ) : null}
        {levelPackage ? (
          <>
            <Descriptions.Item label="会员月数">
              {formatValue(levelPackage.level_month_count)}
            </Descriptions.Item>
            <Descriptions.Item label="权益开始时间">
              {formatDateTime(levelPackage.level_period_start)}
            </Descriptions.Item>
            <Descriptions.Item label="计划结束时间">
              {formatDateTime(levelPackage.planned_level_period_end)}
            </Descriptions.Item>
          </>
        ) : null}
      </Descriptions>
    </section>
  )
}

export const PaymentDetails = ({
  payment,
}: {
  payment?: API.User.PaymentTrade | null
}) => {
  return (
    <section className={styles.detailSection}>
      <Title
        className={styles.detailTitle}
        level={4}
      >
        支付记录
      </Title>
      {payment ? (
        <Descriptions
          bordered
          column={3}
          size="small"
        >
          <Descriptions.Item label="支付记录ID">
            {formatValue(payment.id)}
          </Descriptions.Item>
          <Descriptions.Item label="关联订单ID">
            {payment.order_id}
          </Descriptions.Item>
          <Descriptions.Item label="订单号">
            {formatValue(payment.order_no)}
          </Descriptions.Item>
          <Descriptions.Item label="支付方式">
            {getEnumLabel(paymentMethodLabel, payment.payment_method)}
          </Descriptions.Item>
          <Descriptions.Item label="支付状态">
            {getEnumLabel(paymentStatusLabel, payment.status)}
          </Descriptions.Item>
          <Descriptions.Item label="支付金额">
            {formatAmount(payment.amount)}
          </Descriptions.Item>
          <Descriptions.Item label="支付时间">
            {formatDateTime(payment.paid_at)}
          </Descriptions.Item>
          <Descriptions.Item label="过期时间">
            {formatDateTime(payment.expires_at)}
          </Descriptions.Item>
          <Descriptions.Item label="已退款金额">
            {formatAmount(payment.refunded_amount)}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="未查询到关联支付记录"
        />
      )}
    </section>
  )
}

export const RechargeDetails = ({
  records,
}: {
  records: API.User.WalletRechargeRecord[]
}) => {
  return (
    <section className={styles.detailSection}>
      <Title
        className={styles.detailTitle}
        level={4}
      >
        积点充值记录
      </Title>
      <Table
        columns={rechargeColumns}
        dataSource={records}
        locale={{ emptyText: "未查询到关联积点充值记录" }}
        pagination={false}
        rowKey={(record) =>
          record.id || `${record.user_id}-${record.effective_at}`
        }
        scroll={{ x: 1_200 }}
        size="small"
      />
    </section>
  )
}
